import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import { useModal } from "@/components/core/modals/use-modal";
import Pagination from "@/components/core/pagination";
import { Content } from "@/components/layouts/content";
import { EditSiteModal } from "@/components/sites/edit-site-modal";
import { sitesQueryOptions } from "@/features/dashboard/api/queries/site-query";
import siteService from "@/features/dashboard/api/services/site-service";
import type { Site } from "@/features/dashboard/types/site";
import { queryClient } from "@/query-client";
import { IconPlus } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";

const columns: ColumnDef<Site>[] = [
	{
		id: "site_code",
		label: "Site Code",
		cellClassName: "min-w-[120px] text-left",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => (
			<Link to={`/sites/${data.id}`} className="flex flex-col font-semibold">
				<span className="text-blue-600">{data.site_code}</span>
			</Link>
		),
	},
	{
		id: "kit_number",
		label: "Kit Number",
		cellClassName: "min-w-[100px]",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span className="text-gray-900">{data.kit_number || "-"}</span>,
	},
	{
		id: "locality",
		label: "Locality",
		cellClassName: "min-w-[120px]",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span className="text-gray-900">{data.locality || "-"}</span>,
	},
	{
		id: "province",
		label: "Province",
		cellClassName: "min-w-[100px]",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span className="text-gray-900">{data.province || "-"}</span>,
	},
	{
		id: "description",
		label: "Project Name",
		cellClassName: "min-w-[150px]",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span className="text-gray-900">{data.project_name || "-"}</span>,
	},
	{
		id: "status",
		label: "Status",
		cellClassName: "",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => {
			const statusColors = {
				active: "bg-green-100 text-green-800",
				disabled: "bg-gray-100 text-gray-800",
			};
			const status = data.is_active ? "active" : "disabled";
			return <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status]}`}>{status.charAt(0).toUpperCase() + status.slice(1)}</span>;
		},
	},
];

export const Route = createFileRoute("/_dashboard/sites")({
	validateSearch: z.object({
		search: z.string().optional(),
		per_page: z.number().optional(),
		status: z.string().optional(),
		page: z.number().nullable().optional(),
	}),
	component: Page,
});

function Page() {
	const navigate = useNavigate();
	const search = Route.useSearch();
	const [editingSite, setEditingSite] = useState<Site | null>(null);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);

	const sitesQuery = useQuery(
		sitesQueryOptions({
			...search,
		}),
	);

	const handleEditSite = (site: Site) => {
		setEditingSite(site);
		setIsEditModalOpen(true);
	};

	const handleDeleteSite = async (site: Site) => {
		await siteService.deleteById(site.id);
		await queryClient.invalidateQueries({
			queryKey: ["sites"],
		});
	};

	const handleCloseEditModal = () => {
		setIsEditModalOpen(false);
		setEditingSite(null);
	};

	const handleEditSuccess = () => {
		sitesQuery.refetch();
	};

	const sites = sitesQuery.data?.data || [];
	const meta = sitesQuery.data?.meta || {
		current_page: 1,
		last_page: 1,
		from: 1,
		to: 0,
		total: 0,
		per_page: 10,
		status_count: {},
	};

	if (sitesQuery.isLoading) {
		return (
			<Content pageName="Sites">
				<div className="flex items-center justify-center h-64">
					<div className="text-center">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
						<p className="mt-2 text-sm text-gray-600">Loading sites...</p>
					</div>
				</div>
			</Content>
		);
	}

	return (
		<Content pageName="Sites">
			{/*<Header title="Sites" />*/}
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="flex flex-row items-center justify-between w-full gap-4">
					<div className="inline-flex flex-col w-auto">
						<span className="text-xl text-foreground-dark font-semibold">Sites</span>
						<span className="text-base text-foreground/70">Manage and monitor your sites</span>
					</div>
					<Link to="/sites/create">
						<Button>
							<IconPlus size={18} stroke={2} />
							<span className="">Add New</span>
						</Button>
					</Link>
				</div>

				<div className="flex flex-col w-full gap-4">
					<>
						<DataTable
							className="border-collapse"
							data={sites}
							columns={columns}
							stickyHead={false}
							stickyCol={false}
							showBorder={true}
							compact={true}
							showToolbar={false}
							onEdit={handleEditSite}
							onDelete={(site) => {
								handleDeleteSite(site);
							}}
						/>
						<Pagination
							onClick={async (page) => {
								await navigate({
									to: "/sites",
									search: (old) => ({
										...old,
										page,
									}),
								});
							}}
							initialPage={meta.current_page}
							pageCount={meta.last_page}
						/>
					</>
				</div>
			</div>
			{editingSite && <EditSiteModal site={editingSite} isOpen={isEditModalOpen} onClose={handleCloseEditModal} onSuccess={handleEditSuccess} />}
		</Content>
	);
}
